import React, { JSX, Suspense, useContext, useEffect, useRef, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { wrapPromise } from "go-core";
import { LoadingContainer } from "go-core/components/Loading";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import ReportData from "go-report/reports";
import {
	getChartReport,
	getChartType,
	getLastMonthFilterBetween,
	getOrganizationIds,
	getOrganizationIdsFromReportFetchRequest,
	getReport,
	parseOrganizationIds,
} from "go-report/reports/services/report-service";
import { getSelectedSegmentForReportConfig } from "go-report/reports/services/segment-service";
import { ReportConfig } from "go-report/reports/services/types";
import { selectUser } from "go-security/services/users/selectors";
import { UserState } from "go-security/services/users/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { apiReport } from "../../../../../../../../services/Api/Report/apiReport";
import { ReportKdsUtils } from "../../../../../../../../utils/reports/ReportKdsUtils";
import ReportShareUtils from "../../../../../../../../utils/reports/ReportShareUtils";

interface ListState {
	resource?: Record<string, any>;
	user: UserState;
	setOrganizationsIds: (organizationIds: number[]) => void;
}

const listName = "KDS_REPORT";
const resourceType = "KDS_REPORT";
const type = "order_item_preparation_status_change";

const Report = ({ resource, user, setOrganizationsIds }: ListState): JSX.Element | null => {
	const { t } = useTranslation();
	const segmentContext = useContext(SegmentContext);
	const filtersParams = useRef<string>("");

	if (!resource) return null;

	const data = resource.read();
	const columnsFromLocalStorage = window.localStorage.getItem(`go_report.employees.columns`)?.split(",");

	let config = {
		reportConfigId: "kds_report",
		aggregatePrefix: "sales",
		type: "bill",
		chart: {
			title: t("modules.preparation_status_report.field.report.title"),
			lineTitle: t("modules.preparation_status_report.field.report_chart.title"),
			aggregate: "average_total_time",
			type: getChartType(),
			parseGroup: (group: string) => {
				if (group === "HOUR_OF_DAY") return "ORDER_ITEM_CREATED_AT_HOUR";
				if (group === "DAY_OF_MONTH") return "ORDER_ITEM_CREATED_AT_DAY";
				if (group === "DATE") return "ORDER_ITEM_CREATED_AT";
				if (group === "DAY_OF_WEEK") return "ORDER_ITEM_CREATED_AT_DAY_OF_WEEK";
				if (group === "MONTH") return "ORDER_ITEM_CREATED_AT_MONTH";
				return group;
			},
		},
		externalSelectedFilters: [
			{
				id: "date_range",
				filterType: "date",
				value: getLastMonthFilterBetween().split("=")[1],
				condition: getLastMonthFilterBetween().split("=")[0],
				visible: true,
			},
			{
				id: "organization_ids",
				filterType: "list",
				condition: "e",
				value: getOrganizationIds(user.organizations?.map((x) => x.id).join(FILTER_VALUE_SEPARATOR)),
				visible: true,
			},
		],
		groups: [
			...ReportShareUtils.getGroupReportCommonGroups(t),
			...ReportKdsUtils.getKdsGroups(t),
			...ReportKdsUtils.getGroups(t),
			...ReportShareUtils.getGroups(t),
		],
		selectedColumns: columnsFromLocalStorage ? columnsFromLocalStorage : ReportKdsUtils.getSelectedColumns(),
		selectedGroups: ["NONE"],
		groupsInfo: {
			...ReportKdsUtils.getGroupsInfo(t),
			...ReportKdsUtils.getKdsGroupsInfo(t),
		},
		showPercent: true,
		columns: [...ReportKdsUtils.getColumns(t), ...ReportKdsUtils.getKdsColumns(t)],
		filters: [
			...ReportShareUtils.getFilters(t, user),
			...ReportKdsUtils.getKdsFilters(t, true, filtersParams, type),
			...ReportKdsUtils.getFilters(t, true, filtersParams, type),
		],
		selectedSegment: getSelectedSegmentForReportConfig(data.segments, "all"),
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
				groups: ["NONE"],
				columns: columnsFromLocalStorage ? columnsFromLocalStorage : ReportKdsUtils.getSelectedColumns(),
				filters: [
					{
						filterId: "date_range",
						filterType: "date",
						value: getLastMonthFilterBetween().split("=")[1],
						condition: getLastMonthFilterBetween().split("=")[0],
					},
					{
						filterId: "organization_ids",
						filterType: "list",
						condition: "e",
						value: getOrganizationIds(user.organizations?.map((x) => x.id).join(FILTER_VALUE_SEPARATOR)),
					},
				],
			},
		],
		fetch: (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			setOrganizationsIds(getOrganizationIdsFromReportFetchRequest(params.f));
			filtersParams.current = params.f;

			return Promise.all([
				getReport(
					apiReport.getReportsCustom,
					params,
					getOrganizationIdsFromReportFetchRequest(params.f),
					config.filters,
					type,
					sourceToken,
					params.groups === "NONE"
				),
				getChartReport(
					apiReport.getReportsCustom,
					params,
					"NONE",
					getOrganizationIdsFromReportFetchRequest(params.f),
					config.filters,
					type,
					sourceToken
				),
			]);
		},
		fetchChartData: (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			return getChartReport(
				apiReport.getReportsCustom,
				params,
				"NONE",
				getOrganizationIdsFromReportFetchRequest(params.f),
				config.filters,
				type,
				sourceToken
			);
		},
		saveSegment: (segment: SegmentType) => {
			return segmentContext.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: t("modules.preparation_status_report.header.title"),
			filename: t("modules.preparation_status_report.field.export_config_filename.title"),
			pdfOrientation: "ASK",
		},
	} as ReportConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	return <ReportData config={config} />;
};

const UserReportsKdsPage = (): JSX.Element => {
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContext = useContext(SegmentContext);
	const user = useSelector(selectUser);
	const [organizationsIds, setOrganizationsIds] = useState<number[]>(() =>
		parseOrganizationIds(user.organizations ?? [])
	);
	const organizationIdsAsString = organizationsIds.join();

	useEffect(() => {
		const params: Record<string, any> = {};
		if (organizationsIds?.length > 0) {
			params.organization_id = organizationIdsAsString;
		}

		setResource(wrapPromise(segmentContext.get(listName, resourceType, undefined, params)));
	}, [organizationIdsAsString]);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<Report resource={resource} user={user} setOrganizationsIds={setOrganizationsIds} />
		</Suspense>
	);
};

export default UserReportsKdsPage;
